import {
  ProcedureExecutionSnapshotSchema,
  type ProcedureExecutionSnapshotStep,
} from '@erp/shared-types';
import { T, useGT, Var } from 'gt-react';
import { MetaRow } from './CodeBlock';
import { SectionHeading, ThinkingSection } from './TraceDetailSections';
import type { Span } from './types';

/** How a planned step was expected to run, as a short translated label. */
function PlannedMode({
  mode,
}: {
  mode: ProcedureExecutionSnapshotStep['plannedMode'];
}) {
  const gt = useGT();
  if (!mode) return null;
  const label: Record<NonNullable<typeof mode>, string> = {
    deterministic: gt('Recorded code', {
      context:
        'procedure step planned mode: replays captured code deterministically',
    }),
    credential_fill: gt('Credential fill', {
      context:
        'procedure step planned mode: types a stored credential by reference',
    }),
    agentic: gt('Agent follows instruction', {
      context:
        'procedure step planned mode: no recorded code, the agent performs the instruction',
    }),
    branch: gt('Decision', {
      context: 'procedure step planned mode: a branch that chooses a path',
    }),
  };
  return (
    <span className="ml-1 rounded border border-stone-200 px-1 py-px text-[9.5px] uppercase tracking-wide text-stone-600">
      {label[mode]}
    </span>
  );
}

/** The recorded outcome of a planned step, read only from the step's own evidence. */
function StepOutcome({ recorded }: { recorded: Span | undefined }) {
  if (!recorded)
    return (
      <T context="procedure step without outcome evidence">
        No Recorded Outcome
      </T>
    );
  if (recorded.sopStepNoop)
    return (
      <T context="procedure step explicitly recorded as an optional no-op">
        Skipped: Target Absent
      </T>
    );
  if (recorded.status === 'failed')
    return <T context="procedure step outcome">Failed</T>;
  if (recorded.status === 'running')
    return <T context="procedure step outcome">Running</T>;
  return <T context="procedure step outcome">Completed</T>;
}

/** One planned step: name, planned mode, outcome, and — when recorded — its authored instruction,
 * constraints, the parameter values it ran with and the path a branch took. */
function PlannedStep({
  step,
  recorded,
}: {
  step: ProcedureExecutionSnapshotStep;
  recorded: Span | undefined;
}) {
  const params = Object.entries(step.params ?? {});
  return (
    <li className="rounded-md border border-stone-200 px-2.5 py-1.5">
      <div className="text-[12px] text-stone-900">
        <span className="font-mono text-stone-500">{step.index}.</span>{' '}
        <Var>{step.name}</Var>
        <PlannedMode mode={step.plannedMode} />
        {' — '}
        <StepOutcome recorded={recorded} />
      </div>
      {step.description && (
        <p className="mt-1 whitespace-pre-wrap text-[11.5px] leading-relaxed text-stone-700">
          {step.description}
        </p>
      )}
      {(step.constraints ?? []).length > 0 && (
        <ul className="mt-1 list-inside list-disc text-[11px] text-stone-600">
          {(step.constraints ?? []).map((constraint, position) => (
            // A step may repeat a constraint's text, so the position disambiguates the key; the
            // list is a static snapshot that never reorders.
            <li key={`${position}:${constraint}`}>{constraint}</li>
          ))}
        </ul>
      )}
      {params.length > 0 && (
        <div className="mt-1">
          {params.map(([key, value]) => (
            <MetaRow key={key} label={key} value={value} mono />
          ))}
        </div>
      )}
      {recorded?.sopBranch && (
        <p className="mt-1 text-[11px] text-stone-700">
          <T context="procedure branch step: the path the replay chose">
            Path taken: <Var>{recorded.sopBranch.label}</Var> (
            <Var>{recorded.sopBranch.mode}</Var>)
          </T>
        </p>
      )}
    </li>
  );
}

/** The main agent's inline call to run this procedure: why, and the result it got back. */
function ProcedureCall({ span }: { span: Span }) {
  const gt = useGT();
  const call = span.procedureCall;
  if (!call) return null;
  return (
    <div className="flex flex-col gap-2">
      {call.reasoning && (
        <div>
          <SectionHeading>
            {gt('Why the agent ran it', {
              context:
                'procedure detail section: the main agent’s reasoning for running this procedure',
            })}
          </SectionHeading>
          <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-stone-800">
            {call.reasoning}
          </p>
        </div>
      )}
      <ThinkingSection thinking={call.thinking} />
      {call.summary && (
        <div>
          <SectionHeading>
            {gt('Result returned to the agent', {
              context:
                'procedure detail section: the result text the procedure handed back to the main agent',
            })}
          </SectionHeading>
          <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-stone-800">
            {call.summary}
          </p>
        </div>
      )}
    </div>
  );
}

/** Compare the recorded execution plan with step evidence without inferring missing outcomes. */
export function ProcedureExecutionEvidence({ span }: { span: Span }) {
  const gt = useGT();
  const parsed = ProcedureExecutionSnapshotSchema.safeParse(
    span.toolInput?.procedureSnapshot,
  );
  if (!parsed.success)
    return (
      <div className="flex flex-col gap-3">
        <ProcedureCall span={span} />
        <p>
          <T context="procedure trace missing historical snapshot">
            The execution plan was not recorded for this run. Only recorded
            steps are shown.
          </T>
        </p>
      </div>
    );
  const snapshot = parsed.data;
  const parameters = Object.entries(snapshot.parameters ?? {});
  return (
    <div className="flex flex-col gap-3">
      <p>
        <T context="executed procedure version">
          Procedure: <Var>{snapshot.title}</Var> · Version{' '}
          <Var>{snapshot.version ?? '—'}</Var>
        </T>
      </p>
      <ProcedureCall span={span} />
      {parameters.length > 0 && (
        <div>
          <SectionHeading>
            {gt('Bound parameters', {
              context:
                'procedure detail section: the parameter values this procedure run was bound to',
            })}
          </SectionHeading>
          <div className="rounded-lg border border-stone-200 px-2.5 py-1">
            {parameters.map(([key, value]) => (
              <MetaRow key={key} label={key} value={value} mono />
            ))}
          </div>
        </div>
      )}
      <p>
        <T context="procedure execution evidence">
          This procedure was replayed. Step results below describe the recorded
          evidence, not the overall task outcome.
        </T>
      </p>
      <ol className="flex flex-col gap-2">
        {snapshot.steps.map((step) => (
          <PlannedStep
            key={`${step.index}:${step.id}`}
            step={step}
            recorded={span.children?.find(
              (child) => child.sopStepIndex === step.index,
            )}
          />
        ))}
      </ol>
    </div>
  );
}
